import React, { useState, useEffect } from 'react';
import { useGetStoreQuery, useUpdateStoreMutation, useCheckSlugQuery } from '../../services/api';
import { validateEmail, validateMobile, debounce } from '../../utils/helpers';
import ProtectedRoute from '../../components/ProtectedRoute/ProtectedRoute';
import Loading from '../../components/Loading/Loading';
import Error from '../../components/Error/Error';
import './StoreSettings.css';

const StoreSettings = () => {
  const { data, isLoading, error, refetch } = useGetStoreQuery();
  const [updateStore, { isLoading: isUpdating }] = useUpdateStoreMutation();
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    themeColor: '#3B82F6'
  });
  const [logo, setLogo] = useState(null);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [slugCheck, setSlugCheck] = useState({});

  const store = data?.data?.restaurant;

  // Debounced slug check
  const debouncedSlugCheck = React.useMemo(
    () => debounce((name) => {
      if (name && name.length >= 2 && name !== store?.name) {
        setSlugCheck({ loading: true });
      }
    }, 500),
    [store?.name]
  );

  // Check slug availability when restaurant name changes
  useEffect(() => {
    if (formData.name && formData.name.length >= 2 && formData.name !== store?.name) {
      debouncedSlugCheck(formData.name);
    }
  }, [formData.name, store?.name, debouncedSlugCheck]);

  const { data: slugData, isLoading: checkingSlug } = useCheckSlugQuery(
    formData.name, 
    { skip: !formData.name || formData.name.length < 2 || formData.name === store?.name }
  );

  useEffect(() => {
    if (slugData) {
      setSlugCheck(slugData.data);
    }
  }, [slugData]);

  // Initialize form with store data
  useEffect(() => {
    if (store) {
      setFormData({
        name: store.name || '',
        address: store.address || '',
        themeColor: store.themeColor || '#3B82F6'
      });
    }
  }, [store]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors and success message
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setSuccess('');
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, logo: 'File size must be less than 5MB' }));
        return;
      }
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, logo: 'Please select an image file' }));
        return;
      }
      setLogo(file);
      setErrors(prev => ({ ...prev, logo: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Restaurant name is required';
    } else if (formData.name.length < 2) {
      newErrors.name = 'Restaurant name must be at least 2 characters';
    } else if (slugCheck.reserved) {
      newErrors.name = 'This restaurant name is reserved. Please choose another name.';
    } else if (!slugCheck.available && !checkingSlug && formData.name !== store?.name) {
      newErrors.name = 'Restaurant name is not available. Please choose another name.';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const submitData = {
        ...formData,
        logo: logo
      };

      await updateStore(submitData).unwrap();
      setSuccess('Store settings updated successfully!');
      refetch();
    } catch (error) {
      setErrors({ 
        submit: error.data?.message || 'Failed to update store settings' 
      });
    }
  };

  const getSlugMessage = () => {
    if (formData.name === store?.name) {
      return null;
    }
    
    if (checkingSlug) {
      return { type: 'info', text: 'Checking availability...' };
    }
    
    if (slugCheck.available) {
      return { type: 'success', text: `Your new shop URL will be: ${slugCheck.suggestedSlug}` };
    }
    
    if (slugCheck.reserved) {
      return { type: 'error', text: 'This name is reserved. Please choose another.' };
    }
    
    if (slugCheck.suggestedSlug && !slugCheck.available) {
      return { type: 'info', text: `Suggested: ${slugCheck.suggestedSlug}` };
    }
    
    return null;
  };

  const slugMessage = getSlugMessage();

  if (isLoading) {
    return <Loading text="Loading store settings..." />;
  }

  if (error) {
    return <Error message="Failed to load store settings" onRetry={refetch} />;
  }

  return (
    <ProtectedRoute roles={['OWNER']}>
      <div className="container">
        <div className="page-header">
          <h1>Store Settings</h1>
        </div>

        {success && (
          <div className="alert alert-success">
            {success}
          </div>
        )}

        {errors.submit && (
          <div className="alert alert-error">
            {errors.submit}
          </div>
        )}

        <div className="settings-grid">
          {/* Store Information */}
          <div className="card">
            <div className="card-header">
              <h2>Store Information</h2>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Shop URL</label>
                  <div className="shop-url-display">
                    <code>https://cafe.webready24.com/{store?.shopSlug}</code>
                  </div>
                  <div className="form-text">
                    This is your unique store URL that customers will use to access your menu
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Restaurant Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-control ${errors.name ? 'error' : ''}`}
                    placeholder="Enter your restaurant name"
                  />
                  {errors.name && (
                    <div className="form-error">{errors.name}</div>
                  )}
                  {slugMessage && (
                    <div className={`form-text ${slugMessage.type}`}>
                      {slugMessage.text}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Address *</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    rows="3"
                    className={`form-control ${errors.address ? 'error' : ''}`}
                    placeholder="Enter your restaurant address"
                  />
                  {errors.address && (
                    <div className="form-error">{errors.address}</div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Theme Color</label>
                  <div className="color-input">
                    <input
                      type="color"
                      name="themeColor"
                      value={formData.themeColor}
                      onChange={handleChange}
                      className="color-picker"
                    />
                    <span className="color-value">{formData.themeColor}</span>
                  </div>
                  <div className="form-text">
                    This color will be used for your store's theme
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Restaurant Logo</label>
                  <div className="logo-upload">
                    {store?.logoUrl && (
                      <div className="current-logo">
                        <img 
                          src={`${store.logoUrl}`} 
                          alt="Current logo"
                          className="logo-preview"
                        />
                        <div className="logo-info">
                          Current logo
                        </div>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="form-control"
                    />
                    {errors.logo && (
                      <div className="form-error">{errors.logo}</div>
                    )}
                    <div className="form-text">
                      Recommended: Square image, max 5MB. Supported formats: JPG, PNG, JPEG
                    </div>
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isUpdating}
                >
                  {isUpdating ? 'Updating...' : 'Update Settings'}
                </button>
              </form>
            </div>
          </div>

          {/* Store Preview */}
          <div className="card">
            <div className="card-header">
              <h2>Store Preview</h2>
            </div>
            <div className="card-body">
              <div className="store-preview">
                <div 
                  className="preview-header"
                  style={{ backgroundColor: formData.themeColor }}
                >
                  {store?.logoUrl && (
                    <img 
                      src={`${store.logoUrl}`} 
                      alt="Store logo"
                      className="preview-logo"
                    />
                  )}
                  <h3 className="preview-name">{formData.name || 'Your Restaurant Name'}</h3>
                </div>
                <div className="preview-content">
                  <p className="preview-address">
                    {formData.address || 'Your restaurant address will appear here'}
                  </p>
                  <div className="preview-url">
                    <strong>URL:</strong> https://cafe.webready24.com/{store?.shopSlug}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default StoreSettings;